import { Alert, Button } from '@ntconsult/aluma-react';
import { Check, Copy, Mail, RotateCcw, UserMinus, UserPlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SituacaoParticipante } from '../types/Painel';

type Props = {
  situacao: SituacaoParticipante;
  /** Quantas pessoas a seleção cobre (ids marcados, ou o total do filtro). */
  selecionados: number;
  /** Total que casa com os filtros e `porPagina`, para o aviso de "selecionar todos". */
  total: number;
  porPagina: number;
  /** Linhas da página atual (todas marcadas quando o aviso aparece). */
  naPagina: number;
  paginaInteiraMarcada: boolean;
  todosDoFiltro: boolean;
  podeIncluir: boolean;
  /** Selecionados (por ids) que não aparecem na página atual (spec 25 R5). */
  foraDaLista: number;
  /** Botões das specs 15 (Exportar) e 16 (nada) e o seletor de colunas entram aqui. */
  extras?: ReactNode;
  onSelecionarTodos: () => void;
  onVerSelecionados: () => void;
  onLimpar: () => void;
  onAcaoLote: () => void;
  /** Enviar o convite à seleção (spec 25 R12); `undefined` quando o envio não está disponível. */
  onEnviarEmail?: () => void;
  /** Copiar os e-mails da seleção (spec 31 R14); `copiados` = quantos, enquanto mostra o check. */
  onCopiarEmails: () => void;
  copiados: number | null;
  /** Área de transferência recusada: o campo para Ctrl+C, logo abaixo da barra (R7). */
  textoManual?: ReactNode;
  onAdicionar: () => void;
};

/** Barra de ações acima da tabela: seleção em lote, adicionar e os botões extras. `aria-live` anuncia a seleção. */
export function ParticipantesBarra({
  situacao,
  selecionados,
  total,
  porPagina,
  naPagina,
  paginaInteiraMarcada,
  todosDoFiltro,
  podeIncluir,
  foraDaLista,
  extras,
  onSelecionarTodos,
  onVerSelecionados,
  onLimpar,
  onAcaoLote,
  onEnviarEmail,
  onCopiarEmails,
  copiados,
  textoManual,
  onAdicionar,
}: Props) {
  const { t } = useTranslation(['participantes', 'convites']);
  const excluir = situacao === 'elegivel';
  const oferecerTodos = paginaInteiraMarcada && !todosDoFiltro && total > porPagina;

  return (
    <div className="flex flex-col gap-2 lg:flex-1">
      <div
        className="flex flex-wrap items-center gap-2"
        role="toolbar"
        aria-label={t('lote.barra')}
      >
        <p aria-live="polite" className="text-sm font-medium text-foreground">
          {selecionados > 0 && t('lote.selecionados', { count: selecionados })}
          {todosDoFiltro && ` ${t('lote.todosDoFiltroAtivo')}`}
          {foraDaLista > 0 && ` · ${t('lote.foraDaLista', { count: foraDaLista })}`}
        </p>
        {selecionados > 0 ? (
          <>
            {excluir && onEnviarEmail && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Mail size={14} />}
                onClick={onEnviarEmail}
              >
                {t('lote.enviarEmail')}
              </Button>
            )}
            {excluir && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={copiados !== null ? <Check size={14} /> : <Copy size={14} />}
                onClick={onCopiarEmails}
              >
                {copiados !== null
                  ? t('convites:lembrar.copiados', { count: copiados })
                  : t('convites:lembrar.copiarEmails')}
              </Button>
            )}
            {/* Excluir se desfaz com "Devolver": nada aqui é vermelho nem primário (spec 32 R5, R6). */}
            <Button
              variant="secondary"
              size="sm"
              leftIcon={excluir ? <UserMinus size={14} /> : <RotateCcw size={14} />}
              onClick={onAcaoLote}
            >
              {t(excluir ? 'lote.excluir' : 'lote.devolver')}
            </Button>
            {!todosDoFiltro && (
              <Button variant="secondary" size="sm" onClick={onVerSelecionados}>
                {t('lote.verSelecionados')}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onLimpar}>
              {t('lote.limpar')}
            </Button>
          </>
        ) : (
          excluir &&
          podeIncluir && (
            <Button
              variant="secondary" // um só primário na página: o do cabeçalho (spec 28 R16)
              size="sm"
              leftIcon={<UserPlus size={14} />}
              onClick={onAdicionar}
            >
              {t('incluir.botao')}
            </Button>
          )
        )}
        {!todosDoFiltro && total >= 1 && (
          <Button variant="secondary" size="sm" onClick={onSelecionarTodos}>
            {t('lote.selecionarTodos', { total })}
          </Button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">{extras}</div>
      </div>
      {textoManual}
      {oferecerTodos && (
        <Alert variant="info" role="status">
          {t('lote.selecionarPagina', { count: naPagina })}{' '}
          <Button variant="secondary" size="sm" onClick={onSelecionarTodos}>
            {t('lote.selecionarTodosFiltro', { total })}
          </Button>
        </Alert>
      )}
    </div>
  );
}
